import React, { useEffect, useRef, useState } from 'react';
import { useStore } from '@/state/store';
import { useI18n } from '@/i18n/I18nContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { downloadText } from '@/lib/pdf';
import { toast } from 'sonner';
import { StickyNote, Upload, Download, Save, ImageOff } from 'lucide-react';

const FIELDS = [
  ['projekt', 'tb_projekt'],
  ['titel', 'tb_titel'],
  ['zeichnungsnummer', 'tb_zeichnungsnummer'],
  ['datum', 'tb_datum'],
  ['ersteller', 'tb_ersteller'],
  ['geprueft', 'tb_geprueft'],
  ['revision', 'tb_revision'],
  ['firma', 'tb_firma'],
];

export const TitleBlockDialog = ({ open, onOpenChange }) => {
  const { t } = useI18n();
  const titleBlock = useStore((s) => s.doc.titleBlock);
  const setTitleBlock = useStore((s) => s.setTitleBlock);
  const saveTemplate = useStore((s) => s.saveTitleBlockTemplate);
  const importTitleBlock = useStore((s) => s.importTitleBlock);
  const [form, setForm] = useState(titleBlock);
  const logoInput = useRef(null);
  const tplInput = useRef(null);

  useEffect(() => {
    if (open) setForm(titleBlock);
  }, [open, titleBlock]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => set('logo', reader.result);
    reader.readAsDataURL(file);
  };

  const onSave = () => {
    setTitleBlock(form);
    onOpenChange(false);
    toast.success(t('saved'));
  };

  const onSaveDefault = () => {
    setTitleBlock(form);
    saveTemplate();
    toast.success(t('saved'));
  };

  const onExportTpl = () => {
    downloadText(JSON.stringify(form, null, 2), 'titleblock-template.json', 'application/json');
  };

  const onImportTpl = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(reader.result);
        setForm((f) => ({ ...f, ...data }));
        importTitleBlock(data);
        toast.success(t('toast_import_ok'));
      } catch (err) {
        toast.error(t('toast_import_err'));
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="title-block-editor">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <StickyNote className="h-5 w-5" /> {t('edit_title_block')}
          </DialogTitle>
          <DialogDescription>{t('app_tagline')}</DialogDescription>
        </DialogHeader>

        <div className="grid max-h-[55vh] grid-cols-2 gap-4 overflow-auto pr-1">
          {FIELDS.map(([key, label]) => (
            <div key={key} className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t(label)}</Label>
              <Input
                value={form[key] || ''}
                onChange={(e) => set(key, e.target.value)}
                className="h-9"
                data-testid={`tb-${key}`}
              />
            </div>
          ))}

          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t('tb_logo')}</Label>
            <div className="flex items-center gap-3">
              <div className="flex h-16 w-28 items-center justify-center rounded-md border border-border bg-white">
                {form.logo ? (
                  <img src={form.logo} alt="logo" className="max-h-14 max-w-24 object-contain" />
                ) : (
                  <ImageOff className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
              <input ref={logoInput} type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="title-block-logo-upload" />
              <Button variant="outline" size="sm" onClick={() => logoInput.current?.click()}>
                <Upload className="mr-2 h-4 w-4" /> {t('upload_logo')}
              </Button>
              {form.logo && (
                <Button variant="ghost" size="sm" onClick={() => set('logo', '')}>
                  {t('remove_logo')}
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={onExportTpl}>
              <Download className="mr-2 h-4 w-4" /> {t('export_template')}
            </Button>
            <input ref={tplInput} type="file" accept="application/json,.json" className="hidden" onChange={onImportTpl} />
            <Button variant="outline" size="sm" onClick={() => tplInput.current?.click()}>
              <Upload className="mr-2 h-4 w-4" /> {t('import_template')}
            </Button>
            <Button variant="outline" size="sm" onClick={onSaveDefault}>
              <Save className="mr-2 h-4 w-4" /> {t('save_as_default')}
            </Button>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={onSave} data-testid="title-block-save-button">
              {t('save')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
